import {
  type SanityProductFragment,
  type SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { getPrice } from '@lib/format'
import { getSanityImageUrl } from '@lib/image'
import { type Locale } from '@lib/language'
import { getProductGalleryPhotos } from '@lib/product'
import { type ProductReview, type ProductReviewSummary } from '@lib/review'
import { getPageUrl } from '@lib/routes'
import { getPortableTextHtml } from './portable-text'
import {
  type AggregateRatingSchema,
  type ProductSchema,
  type ReviewSchema,
} from './types'

/**
 * Gets the first existing value from an array of values.
 */
function getFirstValue<T = string>(values: (T | undefined)[]): T | undefined {
  return values.filter(Boolean)[0]
}

/**
 * Gets aggregate rating schema.
 */
const getAggregateRatingSchema = (
  ratingCount?: number,
  ratingValue?: number,
): AggregateRatingSchema | undefined => {
  if (!ratingCount || !ratingValue) {
    return
  }

  return {
    '@type': 'AggregateRating',
    ratingCount: `${ratingCount}`,
    ratingValue: `${ratingValue}`,
  }
}

/**
 * Gets reviews schema from given reviews.
 */
const getReviewsSchema = (reviews: ProductReview[]) => {
  if (reviews.length === 0) {
    return
  }

  return reviews.map((review) => {
    const reviewSchema: ReviewSchema = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.reviewRating,
      },
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewBody: review.reviewMessage,
    }

    return reviewSchema
  })
}

/**
 * Returns product schema.
 */
export const getProductSchema = (
  locale: Locale,
  productReviews: ProductReview[],
  productReviewSummary: ProductReviewSummary,
  product?: SanityProductFragment,
  variant?: SanityProductVariantFragment,
  siteTitle?: string,
  siteUrl?: string,
  currencyCode?: string,
  taxRate?: number,
) => {
  if (!product || !currencyCode || !taxRate) {
    return
  }

  const description = getPortableTextHtml(product.description)
  const isInStock = variant?.inStock ?? product.inStock
  const price = getPrice(variant?.price ?? product.price, {
    taxRate,
  })
  const comparePrice = getPrice(variant?.comparePrice ?? product.comparePrice, {
    taxRate,
  })
  const domain = siteUrl?.replace(/\/+$/g, '')
  const productUrl = getPageUrl(locale, 'product', product.slug.current, {
    ...(variant
      ? {
          variant: `${variant?.variantID}`,
        }
      : {}),
  })
  const nextYearIsoDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1),
  )
    .toISOString()
    .split('T')[0]

  const getImage = () => {
    const photos = product.galleryPhotos
      ? getProductGalleryPhotos(product.galleryPhotos, variant)
      : []

    if (photos.length === 0) {
      return
    }

    return getSanityImageUrl(photos[0], {
      width: 800,
      fitMax: true,
      autoFormat: true,
    })
  }

  const productSchema: ProductSchema = {
    '@type': 'Product',
    name: product.title,
    description,
    sku: getFirstValue([variant?.sku, product.sku]),
    gtin: getFirstValue([variant?.barcode, product.barcode]),
    brand: {
      '@type': 'Brand',
      name: siteTitle ?? '',
    },
    image: getImage(),
    offers: {
      '@type': 'Offer',
      availability: isInStock
        ? 'http://schema.org/InStock'
        : 'http://schema.org/SoldOut',
      price: comparePrice && Number(comparePrice) > 0 ? comparePrice : price,
      priceCurrency: currencyCode,
      url: `${domain}${productUrl}`,
      priceValidUntil: nextYearIsoDate,
    },
    review: getReviewsSchema(productReviews),
    aggregateRating: getAggregateRatingSchema(
      productReviewSummary.ratingCount,
      productReviewSummary.ratingValue,
    ),
  }

  return JSON.stringify(productSchema)
}
